/** Canonical Bonsai OS hub shell origins allowed to embed guest apps (both IC gateways each). */

export const BONSAI_OS_REGISTRY_CANISTER_ID = "u2jsa-baaaa-aaaau-aghea-cai";
export const BONSAI_OS_LANDING_CANISTER_ID = "2dxs4-nyaaa-aaaau-agzda-cai";
export const IC_LAUNCHPAD_CANISTER_ID = "tnyst-jqaaa-aaaau-afpfq-cai";
export const BONSAI_RADIO_CANISTER_ID = "yfpar-eiaaa-aaaau-agzoa-cai";
export const BAZAAR_MARKET_CANISTER_ID = "iabi6-zqaaa-aaaau-agwpq-cai";
export const CANOPY_WALLET_CANISTER_ID = "7h6n6-eqaaa-aaaau-ag6da-cai";

export const BONSAI_OS_REGISTRY_FRAME_ANCESTORS = [
  `https://${BONSAI_OS_REGISTRY_CANISTER_ID}.icp0.io`,
  `https://${BONSAI_OS_REGISTRY_CANISTER_ID}.ic0.app`,
] as const;

export const BONSAI_OS_LANDING_FRAME_ANCESTORS = [
  `https://${BONSAI_OS_LANDING_CANISTER_ID}.icp0.io`,
  `https://${BONSAI_OS_LANDING_CANISTER_ID}.ic0.app`,
] as const;

/** Custom domain for Bonsai OS landing (ICP custom domains / PWA). */
export const BONSAI_OS_CUSTOM_DOMAIN_FRAME_ANCESTORS = [
  "https://bonsaios.app",
  "https://www.bonsaios.app",
] as const;

export const IC_LAUNCHPAD_FRAME_ANCESTORS = [
  `https://${IC_LAUNCHPAD_CANISTER_ID}.icp0.io`,
  `https://${IC_LAUNCHPAD_CANISTER_ID}.ic0.app`,
] as const;

export const BONSAI_RADIO_FRAME_ANCESTORS = [
  `https://${BONSAI_RADIO_CANISTER_ID}.icp0.io`,
  `https://${BONSAI_RADIO_CANISTER_ID}.ic0.app`,
] as const;

export const BAZAAR_MARKET_FRAME_ANCESTORS = [
  `https://${BAZAAR_MARKET_CANISTER_ID}.icp0.io`,
  `https://${BAZAAR_MARKET_CANISTER_ID}.ic0.app`,
] as const;

export const CANOPY_WALLET_FRAME_ANCESTORS = [
  `https://${CANOPY_WALLET_CANISTER_ID}.icp0.io`,
  `https://${CANOPY_WALLET_CANISTER_ID}.ic0.app`,
] as const;

export const OPENCHAT_FRAME_ANCESTORS = ["https://oc.app"] as const;

export const THE_SWOP_CANISTER_ID = "5axgh-oqaaa-aaaac-qgeoa-cai";

export const THE_SWOP_FRAME_ANCESTORS = [
  `https://${THE_SWOP_CANISTER_ID}.icp0.io`,
  `https://${THE_SWOP_CANISTER_ID}.ic0.app`,
] as const;

export const LOCAL_DEV_FRAME_ANCESTORS = [
  "http://127.0.0.1:*",
  "http://localhost:*",
] as const;

/** All hosts for CSP frame-ancestors on guest IC asset canisters. */
export const BONSAI_OS_EMBED_ANCESTORS = [
  "'self'",
  ...BONSAI_OS_REGISTRY_FRAME_ANCESTORS,
  ...BONSAI_OS_LANDING_FRAME_ANCESTORS,
  ...BONSAI_OS_CUSTOM_DOMAIN_FRAME_ANCESTORS,
  ...IC_LAUNCHPAD_FRAME_ANCESTORS,
  ...BONSAI_RADIO_FRAME_ANCESTORS,
  ...OPENCHAT_FRAME_ANCESTORS,
  ...THE_SWOP_FRAME_ANCESTORS,
  ...LOCAL_DEV_FRAME_ANCESTORS,
] as const;

/** Space-separated directive value (without `frame-ancestors` keyword). */
export function buildFrameAncestorsCspDirective(
  extra: readonly string[] = [],
): string {
  return [...BONSAI_OS_EMBED_ANCESTORS, ...extra].join(" ");
}

/** Merch Hydrogen storefront — embeds Canopy Account collar. */
export const MERCH_STOREFRONT_FRAME_ANCESTORS = [
  "https://t3kno-logic.xyz",
  "https://www.t3kno-logic.xyz",
] as const;

/** Host origins that may receive guest → parent postMessage (never `"*"`). */
export const BONSAI_OS_HOST_POST_ORIGINS: readonly string[] = [
  ...BONSAI_OS_CUSTOM_DOMAIN_FRAME_ANCESTORS,
  ...BONSAI_OS_LANDING_FRAME_ANCESTORS,
  ...BONSAI_OS_REGISTRY_FRAME_ANCESTORS,
  ...IC_LAUNCHPAD_FRAME_ANCESTORS,
  ...BONSAI_RADIO_FRAME_ANCESTORS,
  ...BAZAAR_MARKET_FRAME_ANCESTORS,
  ...MERCH_STOREFRONT_FRAME_ANCESTORS,
];

const HOST_POST_ORIGIN_SET = new Set(
  BONSAI_OS_HOST_POST_ORIGINS.map((o) => o.replace(/\/$/, "")),
);

export function isAllowlistedOsHostOrigin(origin: string): boolean {
  try {
    return HOST_POST_ORIGIN_SET.has(new URL(origin).origin);
  } catch {
    return HOST_POST_ORIGIN_SET.has(origin.replace(/\/$/, ""));
  }
}

/** Prefer `location.ancestorOrigins` / referrer when they match the Hub allowlist. */
export function resolveOsHostOrigin(): string | null {
  if (typeof window === "undefined") return null;
  try {
    const ancestors = window.location.ancestorOrigins;
    if (ancestors && ancestors.length > 0) {
      const first = ancestors.item(0);
      if (first && isAllowlistedOsHostOrigin(first)) {
        return new URL(first).origin;
      }
    }
  } catch {
    /* ignore */
  }
  try {
    if (document.referrer) {
      const origin = new URL(document.referrer).origin;
      if (isAllowlistedOsHostOrigin(origin)) return origin;
    }
  } catch {
    /* ignore */
  }
  return null;
}

/**
 * Guest iframe → Hub parent. Fan-out to allowlisted host origins when the
 * parent origin is not yet known (first handshake). Never uses `"*"`.
 */
export function postOsMessageToParent(
  message: unknown,
  target: Window | null = typeof window !== "undefined" ? window.parent : null,
): void {
  if (typeof window === "undefined" || !target || target === window) return;
  try {
    const origin = resolveOsHostOrigin();
    if (origin) {
      target.postMessage(message, origin);
      return;
    }
    for (const o of BONSAI_OS_HOST_POST_ORIGINS) {
      target.postMessage(message, o);
    }
  } catch {
    /* ignore */
  }
}

/** Registry + landing only — used when documenting minimal host set. */
export const BONSAI_OS_HUB_FRAME_ANCESTORS_CSP = [
  ...BONSAI_OS_REGISTRY_FRAME_ANCESTORS,
  ...BONSAI_OS_LANDING_FRAME_ANCESTORS,
  ...BONSAI_OS_CUSTOM_DOMAIN_FRAME_ANCESTORS,
].join(" ");
